/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Job, ProcessDescription } from 'ogc-processes-components';
import { DataTableFilterMeta, DataTableFilterMetaData, DataTableOperatorFilterMetaData } from 'primereact/datatable';

export class Utils {
  static hideProcessPrefix(processID: string) {
    return processID.replace(/@\d+/g, '').replace(/^\//, '').trim();
  }

  static simpleHashStringSync(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32-bit integer
    }
    return hash.toString(16); // Convert to hexadecimal string
  }

  static buildNewQueryParamsUrl(params: Record<string, string | undefined>, existing?: URLSearchParams): string {
    const _queryParams = existing ?? new URLSearchParams('');
    Object.entries(params || {}).forEach(([key, value]) => {
      if (_queryParams.has(key)) {
        if (!value) {
          _queryParams.delete(key);
        } else {
          _queryParams.set(key, value);
        }
      } else {
        if (value) {
          _queryParams.append(key, value);
        }
      }
    });
    if (_queryParams.size.toString() === '') {
      return '';
    }
    return '?'.concat(_queryParams.toString());
  }

  static buildWithQueryParamsUrl(params: Record<string, string>): string {
    const { href } = window.location;
    let queryString = '';
    const lastQuestionMarkIndex = href.lastIndexOf('?');
    if (lastQuestionMarkIndex !== -1) {
      queryString = href.slice(lastQuestionMarkIndex + 1); // Extract part
    }
    const _queryParams = new URLSearchParams(queryString);
    return this.buildNewQueryParamsUrl(params, _queryParams);
  }

  static filtersToQueryParams(filters: DataTableFilterMeta, model?: string): Record<string, string> {
    let params = {};
    Object.entries(filters).forEach(([key, v]) => {
      const _key = model ? model + '_' + key : key;
      const { value, operator, constraints } = v as DataTableOperatorFilterMetaData & DataTableFilterMetaData;
      if (operator && constraints && constraints.length > 0) {
        params = { ...params, [_key]: constraints[0].value };
      }
      if (value !== undefined) {
        params = { ...params, [_key]: value };
      }
    });
    return params;
  }

  static filterExecutionContent({
    job,
    process
  }: {
    job: Job;
    process: ProcessDescription;
  }): Record<string, unknown> | undefined {
    const launcher_selected = job.execution_content?.['antflow_config:launcher'] as string | undefined;
    const launcher_available = process.inputs?.['antflow_config:launcher'];
    if (launcher_selected && launcher_available) {
      if (launcher_available.schema?.enum?.includes(launcher_selected)) {
        return job.execution_content;
      }
    }

    const override_content: Record<string, unknown> = {};
    for (const input in process.inputs) {
      const processInput = process.inputs[input];
      if (processInput.schema?.format === 'secret') {
        if (!processInput.schema?.enum?.includes(job.execution_content?.[input] as string)) {
          override_content[input] = undefined;
        }
      }
    }
    return { ...job.execution_content, ...override_content, 'antflow_config:launcher': undefined };
  }

  static overrideDescription(process: ProcessDescription, helper: string): ProcessDescription {
    for (const input in process.inputs) {
      const processInput = process.inputs[input];
      if (processInput.schema?.format === 'secret') {
        processInput.description = processInput.description
          ? `${processInput.description || ''}<br/>${helper}`
          : helper;
      }
    }
    return process;
  }
}
